import { useCallback, useEffect, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

const feeSheet1Def: SlotGroupDefinition = {
  label: '3 Bedroom Fee Sheet',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://media.multihub.io/property/6767906449167334/01a0208c-6c2c-776b-ad85-d4bdd9cbbaf9.webp',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'The Deryn 3 bedroom 3 bath fee sheet',
    },
    caption: {
      label: 'Caption',
      type: 'text',
      defaultValue: '3 Bedroom, 3 Bath Fee Sheet',
    },
  },
};

const feeSheet2Def: SlotGroupDefinition = {
  label: 'Co-Living Fee Sheet',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://media.multihub.io/property/6767906449167334/01a0208c-6b8c-75b1-a378-4816e1628bda.webp',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'The Deryn co-living fee sheet',
    },
    caption: {
      label: 'Caption',
      type: 'text',
      defaultValue: 'Co-Living Fee Sheet',
    },
  },
};

export function FeeTransparency() {
  const sheet1 = useSlotGroup('feetransparency.fee_sheet_1', feeSheet1Def);
  const sheet2 = useSlotGroup('feetransparency.fee_sheet_2', feeSheet2Def);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const sheets = [
    {
      image: sheet1.image || '',
      alt: sheet1.alt || 'The Deryn 3 bedroom 3 bath fee sheet',
      caption: sheet1.caption || '3 Bedroom, 3 Bath Fee Sheet',
    },
    {
      image: sheet2.image || '',
      alt: sheet2.alt || 'The Deryn co-living fee sheet',
      caption: sheet2.caption || 'Co-Living Fee Sheet',
    },
  ].filter((sheet) => !!sheet.image);

  const lightboxOpen = lightboxIndex !== null;
  const activeSheet = lightboxOpen ? sheets[lightboxIndex] : null;

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  const goToPrev = useCallback(() => {
    if (lightboxIndex === null || sheets.length <= 1) return;
    setLightboxIndex((lightboxIndex - 1 + sheets.length) % sheets.length);
  }, [lightboxIndex, sheets.length]);

  const goToNext = useCallback(() => {
    if (lightboxIndex === null || sheets.length <= 1) return;
    setLightboxIndex((lightboxIndex + 1) % sheets.length);
  }, [lightboxIndex, sheets.length]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft') goToPrev();
      else if (e.key === 'ArrowRight') goToNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, closeLightbox, goToPrev, goToNext]);

  useEffect(() => {
    if (lightboxOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [lightboxOpen]);

  const openSheet = (imageUrl: string) => {
    const index = sheets.findIndex((sheet) => sheet.image === imageUrl);
    if (index >= 0) setLightboxIndex(index);
  };

  return (
    <section className="bg-background py-16 md:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-12 max-w-3xl text-center">
          <RichTextContent
            path="feetransparency.intro"
            as="div"
            className="font-body text-base leading-relaxed text-muted-foreground [&_p]:mb-0"
          />
        </div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-6 lg:gap-10">
          <EditableSlotGroup
            groupPath="feetransparency.fee_sheet_1"
            definition={feeSheet1Def}
          >
            <figure className="flex flex-col items-center gap-3">
              {sheet1.image ? (
                <>
                  <button
                    type="button"
                    onClick={() => openSheet(sheet1.image!)}
                    className="group w-full cursor-zoom-in text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                    aria-label={`Enlarge ${sheet1.caption || '3 Bedroom, 3 Bath Fee Sheet'}`}
                  >
                    <VibesitesImage
                      url={sheet1.image}
                      alt={sheet1.alt || 'The Deryn 3 bedroom 3 bath fee sheet'}
                      className="h-auto w-full object-contain transition-opacity duration-200 group-hover:opacity-90"
                      sizes="(min-width: 768px) 45vw, 90vw"
                    />
                  </button>
                  <p className="font-body text-xs uppercase tracking-wider text-muted-foreground">
                    Click to enlarge
                  </p>
                </>
              ) : null}
            </figure>
          </EditableSlotGroup>

          <EditableSlotGroup
            groupPath="feetransparency.fee_sheet_2"
            definition={feeSheet2Def}
          >
            <figure className="flex flex-col items-center gap-3">
              {sheet2.image ? (
                <>
                  <button
                    type="button"
                    onClick={() => openSheet(sheet2.image!)}
                    className="group w-full cursor-zoom-in text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                    aria-label={`Enlarge ${sheet2.caption || 'Co-Living Fee Sheet'}`}
                  >
                    <VibesitesImage
                      url={sheet2.image}
                      alt={sheet2.alt || 'The Deryn co-living fee sheet'}
                      className="h-auto w-full object-contain transition-opacity duration-200 group-hover:opacity-90"
                      sizes="(min-width: 768px) 45vw, 90vw"
                    />
                  </button>
                  <p className="font-body text-xs uppercase tracking-wider text-muted-foreground">
                    Click to enlarge
                  </p>
                </>
              ) : null}
            </figure>
          </EditableSlotGroup>
        </div>

        <RichTextContent
          path="feetransparency.disclaimer"
          as="div"
          className="mx-auto mt-12 max-w-3xl text-center font-body text-sm italic leading-relaxed text-muted-foreground [&_p]:mb-0"
        />
      </div>

      {lightboxOpen && activeSheet ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label={activeSheet.caption}
        >
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-black/50 text-white transition-colors duration-200 hover:bg-black/70"
            aria-label="Close enlarged fee sheet"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {sheets.length > 1 ? (
            <div className="absolute top-5 left-1/2 z-20 -translate-x-1/2 font-body text-sm tracking-wide text-white/80">
              {(lightboxIndex ?? 0) + 1} / {sheets.length}
            </div>
          ) : null}

          {sheets.length > 1 ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                goToPrev();
              }}
              className="absolute left-3 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 text-foreground transition-all duration-200 hover:bg-white md:left-5 md:h-11 md:w-11"
              aria-label="Previous fee sheet"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
              </svg>
            </button>
          ) : null}

          {sheets.length > 1 ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              className="absolute right-3 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 text-foreground transition-all duration-200 hover:bg-white md:right-5 md:h-11 md:w-11"
              aria-label="Next fee sheet"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
              </svg>
            </button>
          ) : null}

          <div
            className="relative flex h-[85vh] w-[92vw] max-w-4xl items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <VibesitesImage
              url={activeSheet.image}
              alt={activeSheet.alt}
              className="h-auto max-h-full w-auto max-w-full object-contain"
              sizes="92vw"
            />
          </div>
        </div>
      ) : null}
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FeeTransparency',
  displayName: 'Fee Transparency',
  tokens: {
    'feetransparency.intro': {
      label: 'Intro',
      type: 'richtext',
      defaultValue:
        '<p>We believe in clear, upfront pricing. Review our current fee sheets for 3-bedroom homes and co-living residences below. Fees are subject to change — contact our leasing team for the most up-to-date details.</p>',
    },
    'feetransparency.disclaimer': {
      label: 'Disclaimer',
      type: 'richtext',
      defaultValue:
        '<p>*Risk fee and pet fee apply only when applicable. Contact us at <a href="/contact">our Contact page</a> with any questions about fees or move-in costs.</p>',
    },
  },
  slotGroups: {
    'feetransparency.fee_sheet_1': feeSheet1Def,
    'feetransparency.fee_sheet_2': feeSheet2Def,
  },
};

(FeeTransparency as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default FeeTransparency;
